import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { ArrowRight, CheckCircle, Shield, Users, Award, Clock, Bell, FileCheck, Smartphone, TrendingUp, Zap, Target } from "lucide-react";
import { Footer } from "@/components/Footer";

export default function CertifiedDrivers() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section with Enhanced Gradient */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-700 to-blue-600 text-white py-24 px-4 overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl" style={{ animationDelay: '1s' }} />
        </div>

        <div className="mx-auto max-w-5xl relative z-10">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm px-4 py-2 text-sm font-semibold mb-6 border border-white/20">
              <Shield className="w-4 h-4" />
              Certified Driver Program
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Peace of Mind, Every Ride
            </h1>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-blue-200">
              Shuttlebee's Commitment to Your Child's Safe Journey
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl leading-relaxed">
              At Shuttlebee, your child's safety is not just a priority, it's our absolute commitment. We deeply understand
              that entrusting your child's journey to others is a big decision.
            </p>
          </div>
        </div>
      </section>

      {/* Driver Selection Process */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="font-display text-4xl font-bold mb-6 text-gray-900">Your Peace of Mind: Our Rigorous Driver Selection Process</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-gray-600 leading-relaxed">
              Every journey with Shuttlebee begins with our unwavering commitment to your child's safety.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="hover-lift border-2 hover:border-blue-200 transition-all duration-300 hover:shadow-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center mb-6 shadow-lg">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900">What We Ensure For Your Child's Safety</h3>
                <ul className="space-y-3">
                  {[
                    "Comprehensive background checks",
                    "Impeccable driving records",
                    "Demonstrated commitment to safety and professionalism",
                    "Adherence to all state-specific eligibility standards",
                    "Deep dedication to student well-being"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 hover-lift hover:shadow-2xl transition-all duration-300">
              <CardContent className="pt-8 pb-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center mb-6 shadow-lg">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Shuttlebee University Onboarding Track</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Once a candidate successfully navigates this initial pre-screening, they receive a special invitation to join the
                  <strong className="text-gray-900"> Shuttlebee University Onboarding Track</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  This isn't just a program; it's your assurance that their driver is advancing into specialized, in-depth training
                  designed to make them a certified, trusted professional for your child's ride.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Shuttlebee University Modules */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="mx-auto max-w-7xl px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold mb-4">Shuttlebee University: A Complete Foundation</h2>
            <div className="w-20 h-1 bg-white/30 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Comprehensive training that covers every aspect of safe, compliant student transportation operations.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              { icon: Zap, title: "Kickoff Module", desc: "Program requirements, business setup fundamentals, and regulatory pathways" },
              { icon: Users, title: "Module 1: Introduction", desc: "Safe transport of minors, understanding risks and duty of care principles" },
              { icon: FileCheck, title: "Module 2: Safety & Compliance", desc: "State regulations, credentials, background checks, and vehicle standards" },
              { icon: Award, title: "Module 3: Driver Skills", desc: "Pre-trip inspections, defensive driving, and passenger management" },
              { icon: Smartphone, title: "Module 4: Technology Tools", desc: "Master the Driver Platform, GPS tracking, and parent communication tools" },
              { icon: TrendingUp, title: "Module 5: Insurance & Risk", desc: "Commercial auto requirements and telematics-supported safety protocols" }
            ].map((module, index) => {
              const Icon = module.icon;
              return (
                <div key={index} className="bg-white/5 backdrop-blur-sm border-2 border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover-lift">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center mb-6 shadow-xl">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{module.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{module.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Safety Compliance with Step Cards */}
      <section className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold mb-6 text-gray-900">Peace of Mind: Our Commitment to Your Child's Safe Journey</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We meticulously handle all the necessary legal and safety documentation, making sure every regulatory standard
              is met before your child ever steps into a vehicle.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                number: "01",
                title: "Ensuring Every Ride is Safe",
                items: [
                  "All services fully authorized by state transportation authorities",
                  "Commercial carrier filings meticulously maintained",
                  "Proper business registration verified",
                  "Operating authority documents always up-to-date"
                ],
                icon: Shield,
                color: "blue"
              },
              {
                number: "02",
                title: "Qualified & Caring Drivers",
                items: [
                  "State and national background checks completed",
                  "Mandatory child abuse clearances",
                  "Valid medical examiner's certificate",
                  "Strict drug and alcohol compliance policies",
                  "Motor vehicle records continuously monitored"
                ],
                icon: Users,
                color: "emerald"
              },
              {
                number: "03",
                title: "Safe & Reliable Vehicles",
                items: [
                  "Semi-annual or annual safety inspections",
                  "Age-appropriate car seats and boosters available",
                  "Passenger capacity limits strictly observed",
                  "Comprehensive insurance verification maintained"
                ],
                icon: CheckCircle,
                color: "orange"
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="relative group">
                  <div className={`absolute -inset-1 bg-gradient-to-r from-${item.color}-600 to-${item.color}-400 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300`}></div>
                  <Card className="relative bg-white border-2 hover:border-blue-300 transition-all duration-300 hover-lift h-full">
                    <CardContent className="pt-8 pb-8">
                      <div className="flex items-start gap-4 mb-6">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white font-bold text-xl shadow-lg flex-shrink-0">
                          {item.number}
                        </div>
                        <div className="flex-1">
                          <Icon className="w-8 h-8 text-blue-600 mb-3" />
                        </div>
                      </div>
                      <h3 className="text-xl font-bold mb-4 text-gray-900">{item.title}</h3>
                      <ul className="space-y-2">
                        {item.items.map((listItem, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <span>{listItem}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technology Features */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold mb-4">How Our Technology Keeps Your Child Safe</h2>
            <div className="w-20 h-1 bg-white/30 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Every certified driver is equipped with state-of-the-art technology designed specifically to enhance your child's safety.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: Target,
                title: "Real-Time GPS Tracking",
                description: "Always know exactly where your child is. Our real-time GPS tracking allows you to monitor their location from pickup until safe arrival."
              },
              {
                icon: Bell,
                title: "Instant Communication Hub",
                description: "Stay effortlessly informed. Our platform sends you immediate updates regarding arrival times, potential delays, and important notifications."
              },
              {
                icon: FileCheck,
                title: "Detailed Digital Ride Logs",
                description: "Every journey is meticulously documented. Our digital logs record crucial details like routes, passenger counts, and trip specifics."
              },
              {
                icon: TrendingUp,
                title: "Continuous Safety Monitoring",
                description: "Advanced vehicle telematics continuously monitor driving behaviors and vehicle health, ensuring the highest safety standards."
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="bg-blue-700/30 backdrop-blur-sm border-2 border-blue-400/30 hover:border-blue-300/50 transition-all duration-300 hover-lift">
                  <CardContent className="pt-8 pb-8">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-blue-500/30 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-7 h-7 text-blue-100" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                        <p className="text-blue-100 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Multi-Layered Safety Stats */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold mb-6 text-gray-900">Your Child's Safety: Our Unwavering Promise</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              At Shuttlebee, safety isn't just a priority—it's the foundation of everything we do.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-5xl mx-auto">
            {[
              { number: "100%", label: "Thorough Background Checks", desc: "Every driver undergoes comprehensive state and national background screening" },
              { number: "2X", label: "More Frequent Vehicle Inspections", desc: "Semi-annual safety inspections, exceeding standard requirements" },
              { number: "24/7", label: "Continuous Monitoring & Support", desc: "Round-the-clock real-time safety monitoring and immediate support" }
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-blue-400 rounded-3xl blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
                  <div className="relative bg-white rounded-3xl p-8 border-2 border-blue-200 hover:border-blue-300 transition-all duration-300 hover-lift">
                    <div className="text-6xl font-bold text-blue-600 mb-3">{stat.number}</div>
                    <h3 className="text-xl font-bold mb-3 text-gray-900">{stat.label}</h3>
                    <p className="text-gray-600">{stat.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-blue-200 hover:border-blue-300 transition-all duration-300 hover-lift">
              <CardContent className="pt-8 pb-8">
                <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-white font-bold">✓</span>
                  </div>
                  Before They Drive
                </h3>
                <ul className="space-y-2">
                  {[
                    "Comprehensive background checks",
                    "Verified driving records",
                    "Medical fitness certification",
                    "Strict drug and alcohol compliance",
                    "Thorough vehicle safety inspections",
                    "Full insurance verification"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200 hover:border-orange-300 transition-all duration-300 hover-lift">
              <CardContent className="pt-8 pb-8">
                <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center">
                    <span className="text-white font-bold">∞</span>
                  </div>
                  Continuous Vigilance
                </h3>
                <ul className="space-y-2">
                  {[
                    "Real-time GPS tracking for parents",
                    "Constant monitoring of safe driving habits",
                    "Regular checks to maintain high standards",
                    "Ongoing vehicle maintenance monitoring",
                    "Continuous driver training and support",
                    "Yearly re-certification of all credentials"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-blue-700 to-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl" style={{ animationDelay: '1s' }} />
        </div>

        <div className="mx-auto max-w-4xl px-6 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-6">
            Your Child's Safety: Our Top Priority, Every Ride
          </h2>
          <p className="text-2xl mb-10 text-blue-100">
            Choose Shuttlebee for safe, reliable, and professional student transportation that supports your child's
            education and strengthens our community.
          </p>
          <Link to="/summer-camp-quote">
            <Button size="xl" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl hover:shadow-white/20 hover:-translate-y-1 transition-all duration-300 text-lg px-10 py-6">
              Request Summerside Camps at SCH Transportation
              <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
